import { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL;

interface Props { navigate: (path: string) => void }

export default function ResetPassword({ navigate }: Props) {
  const [token, setToken] = useState<string>('');
  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';
    setToken(t);
    if (!t) setError('Missing reset token. Use the link from your email.');
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pwd1.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pwd1 !== pwd2) { setError('Passwords do not match.'); return; }
    setLoading(true);
    const res = await fetch(`${API}/affiliate-portal/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: pwd1 }),
    });
    const json = (await res.json()) as { success: boolean; error?: string };
    setLoading(false);
    if (!json.success) { setError(json.error || 'Reset failed.'); return; }
    setDone(true);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 font-inter flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Set New Password</h1>

        {done ? (
          <div className="text-center py-6">
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
            <p className="text-slate-700 dark:text-slate-300">Password updated. You can now sign in.</p>
            <button onClick={() => navigate('/login')} className="mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded">Go to Sign In</button>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">New password (min 8 chars)</label>
                <input type="password" value={pwd1} onChange={(e) => setPwd1(e.target.value)} required minLength={8} className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Confirm password</label>
                <input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required minLength={8} className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-3 py-2" />
              </div>
              <button type="submit" disabled={loading || !token} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded font-medium disabled:opacity-50">
                {loading ? 'Updating...' : 'Set Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
